import { useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Show eye toggle for password fields. Defaults to true when type is password. */
  showPasswordToggle?: boolean;
}

export function Input({
  label,
  error,
  hint,
  className,
  id,
  type,
  showPasswordToggle,
  ...props
}: InputProps) {
  const [visible, setVisible] = useState(false);
  const inputId = id || props.name;
  const isPassword = type === 'password';
  const canToggle = isPassword && showPasswordToggle !== false;
  const inputType = canToggle && visible ? 'text' : type;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-brand-navy">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          type={inputType}
          className={cn(
            'block w-full rounded-xl border-gray-200 bg-white px-4 py-2.5 text-sm text-brand-navy shadow-sm focus:border-primary focus:ring-primary',
            canToggle && 'pr-11',
            error && 'border-red-400 focus:border-red-400 focus:ring-red-200',
            className,
          )}
          {...props}
        />
        {canToggle && (
          <button
            type="button"
            onClick={() => setVisible((value) => !value)}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-brand-muted transition-colors hover:text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
          >
            {visible ? <EyeOff className="h-4 w-4" strokeWidth={2} /> : <Eye className="h-4 w-4" strokeWidth={2} />}
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!error && hint && <p className="text-xs text-brand-muted">{hint}</p>}
    </div>
  );
}
